/* Per-chat read state — the single source of truth for "unread message"
 * indicators across the app (sidebar Chats badge, the topbar messages
 * icon, the mobile hamburger dot, and the orange marks in the chat
 * history list).
 *
 * Read state is now persisted SERVER-SIDE (table `chat_reads`, via
 * /api/me/chat-reads), so opening a chat on one browser marks it read on
 * every browser the user signs into. localStorage is kept only as an
 * optimistic cache: it lets the marks render instantly on reload before
 * the server map arrives, and survives an offline session. The server is
 * authoritative — `syncReadsFromServer()` reconciles the two on startup.
 *
 * A chat counts as UNREAD when its `updatedAt` (bumped whenever a new
 * message lands, see touchChat) is newer than the last time the user
 * opened that specific chat (`{ [chatId]: lastOpenedMs }`).
 *
 * A tiny pub/sub lets every surface stay in lock-step: opening a chat in
 * the history list marks it read here, and the sidebar/topbar badges
 * recompute immediately rather than waiting for the next poll.
 */

import { fetchChatReads, postChatRead, postChatReads } from '../api.js';

const LS_KEY = 'erasphere.chatReads';

function load() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}') || {}; }
  catch { return {}; }
}

let reads = load();
const subs = new Set();

function persist() {
  try { localStorage.setItem(LS_KEY, JSON.stringify(reads)); } catch { /* quota / private mode */ }
}
function emit() {
  for (const fn of subs) { try { fn(reads); } catch { /* ignore subscriber errors */ } }
}

/** Subscribe to read-state changes. Returns an unsubscribe fn. */
export function subscribeReads(fn) {
  subs.add(fn);
  return () => subs.delete(fn);
}

/** Current read map (do not mutate). */
export function getReads() { return reads; }

const toMs = d => (d ? new Date(d).getTime() : 0);

/** Mark a chat read up to `at` (default now). No-op if already at/after.
 *  Updates the local cache optimistically and writes through to the server
 *  (fire-and-forget) so the mark propagates to the user's other browsers. */
export function markChatRead(id, at = Date.now()) {
  if (!id || (reads[id] || 0) >= at) return;
  reads = { ...reads, [id]: at };
  persist();
  emit();
  postChatRead(id, at).catch(() => { /* best-effort; cache holds it until next sync */ });
}

/** Is this chat unread? (updatedAt newer than when it was last opened.) */
export function isChatUnread(chat, readsMap = reads) {
  if (!chat) return false;
  return toMs(chat.updatedAt) > (readsMap[chat._id || chat.id] || 0);
}

/** Count unread chats in a list. */
export function countUnread(chats, readsMap = reads) {
  let n = 0;
  for (const c of chats || []) if (isChatUnread(c, readsMap)) n++;
  return n;
}

let synced = false;
/** Reconcile the local cache with the server's read map. The server is
 *  authoritative, but we take the element-wise MAX so a chat just opened
 *  in this tab (already pushed) isn't briefly resurrected as unread, and
 *  any read mark that only ever lived in THIS browser's localStorage gets
 *  pushed up so it reaches the user's other browsers too. Idempotent —
 *  runs once per page load. */
export async function syncReadsFromServer() {
  if (synced) return;
  synced = true;
  let server;
  try {
    server = await fetchChatReads();
  } catch {
    synced = false;  // allow a retry on the next trigger
    return;
  }
  const next = { ...server };
  const localOnly = {};
  for (const [id, at] of Object.entries(reads)) {
    const ms = Number(at) || 0;
    if (ms > (next[id] || 0)) { next[id] = ms; localOnly[id] = ms; }
  }
  reads = next;
  persist();
  emit();
  // Propagate browser-only reads to the server so they sync everywhere.
  if (Object.keys(localOnly).length) {
    postChatReads(localOnly).catch(() => { /* best-effort */ });
  }
}
